import React, { useMemo } from 'react'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import DeviationCell from '../components/DeviationCell'
import ExternalJobRowActions from '../components/ExternalJobRowActions'

// Story 100 (S100/S103, RN37/RN38.1/RN33): the SmartMateriaUITable-shaped column set for the
// pending-dispatch and dispatched populations of the External jobs widget (Sun's
// `ExternalJobTripRowDto`, shared by BOTH `GET .../pendingDispatch` and `GET .../dispatched`). Every
// row identifies the fabrication order, the job and its technology, and shows the promised
// deadline, the actual dispatch, the actual return, who dispatched, who returned, the deviation and
// the row's own action -- fields that do not yet apply for a given row are simply left blank (never
// a placeholder, RN40.1/RN41: blank is not zero).
//
// There is deliberately NO "start deviation" column (RN38.1): the actual dispatch is shown as a
// plain fact, never compared against a theoretical start.
//
// PARITY WITH THE HISTORY (2026-09-02, sun commit 9790585a9e, PR sun#3709): the "Returned"
// population used to read a leaner, differently-named contract (`ReturnedTripDto` before its
// extension), which forced a dedicated `useReturnedJobColumns` hook (removed). Sun's `/returned`
// row now carries the SAME field names as this hook expects (`fabricationOrder`, `job`,
// `technology`, `promisedReturnDate`/`promisedReturnDurationLabel`, `deviationMinutes`) -- the ONLY
// remaining divergence is the actual dates, still named `actualStart`/`actualEnd` on that contract
// (they predate this hook's `ExternalJobTripRowDto` convention). `ReturnedJobsHistory.jsx` renames
// them to `actualDispatch`/`actualReturn` at its `dataFetch` boundary, so this hook never needs to
// know about a per-population field name. `includeActions: false` is how the Returned tab reuses
// this SAME column set with NO actions column at all (RN33/S105: structurally impossible to leak a
// Dispatch/Return button into history, independent of any row-shape ambiguity).
//
// SAT #3089/#3971: every column's `field` is also the key Sun filters and sorts by. The promised
// return and the deviation are DERIVED by Sun at read time (the returned history cannot filter or
// order them in SQL) and the actions column holds no data, so those three neither offer a search box
// (`disableFilter`) nor sort (`sortable: false`): no control that would silently do nothing.
//
// @param {Object} [params]
// @param {Function} [params.onDispatch] - (row) => void
// @param {Function} [params.onReturn] - (row) => void
// @param {boolean} [params.includeActions] - false omits the actions column entirely (Returned tab)
const useExternalJobColumns = ({ onDispatch, onReturn, includeActions = true } = {}) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()

  return useMemo(() => {
    const columns = [
      {
        name: t_('Fabrication order'),
        field: 'fabricationOrder',
        readOnly: true,
        render: (row) => row.fabricationOrder?.fabricationOrder ?? ''
      },
      {
        name: t_('Job'),
        field: 'job',
        readOnly: true,
        render: (row) => row.job?.name ?? ''
      },
      {
        name: t_('Technology'),
        field: 'technology',
        readOnly: true,
        render: (row) => row.technology ?? ''
      },
      {
        name: t_('Promised return'),
        field: 'promisedReturn',
        readOnly: true,
        sortable: false,
        disableFilter: true,
        render: (row) => (row.promisedReturnDate ? formatDateTime(row.promisedReturnDate) : (row.promisedReturnDurationLabel ?? ''))
      },
      {
        name: t_('Actual dispatch'),
        field: 'actualDispatch',
        type: 'datetime',
        readOnly: true,
        render: (row) => (row.actualDispatch ? formatDateTime(row.actualDispatch) : '')
      },
      {
        name: t_('Actual return'),
        field: 'actualReturn',
        type: 'datetime',
        readOnly: true,
        render: (row) => (row.actualReturn ? formatDateTime(row.actualReturn) : '')
      },
      {
        name: t_('Sent by'),
        field: 'sentBy',
        readOnly: true,
        render: (row) => row.sentBy ?? ''
      },
      {
        name: t_('Returned by'),
        field: 'returnedBy',
        readOnly: true,
        render: (row) => row.returnedBy ?? ''
      },
      {
        name: t_('Deviation'),
        field: 'deviation',
        readOnly: true,
        sortable: false,
        disableFilter: true,
        render: (row) => <DeviationCell minutes={row.deviationMinutes} />
      }
    ]

    if (includeActions) {
      columns.push({
        name: '',
        field: 'actions',
        readOnly: true,
        sortable: false,
        disableFilter: true,
        render: (row) => <ExternalJobRowActions row={row} onDispatch={onDispatch} onReturn={onReturn} />
      })
    }

    return columns
  }, [t_, formatDateTime, onDispatch, onReturn, includeActions])
}

export default useExternalJobColumns
